import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { Alert } from '@material-ui/lab';
import { Avatar, CircularProgress, Chip, LinearProgress, Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import clsx from 'clsx';

import { getBadgeIcon, getOnlyFansConnectionId, ONLYFANS_NETWORK_ID } from 'utils/helpers';
import { closeModal, openModal } from 'features/utilsSlice';
import { setInvitation } from 'features/gigsSlice';
import { MAX_INVITATIONS_COUNT } from 'constants/settings';
import NoAvatarImage from 'public/images/no-avatar.svg';
import InfluencerProfileDialog from './profile/InfluencerProfileDialog';
import WorkingStats from './profile/WorkingStats';
import PercentageDifference from './gigs/PercentageDifference';
import SimpleDialog from './SimpleDialog';
import GigInvitationDialog from './gigs/GigInvitationDialog';
import NetworksWithFollowers from './NetworksWithFollowers';
import FollowProfile from 'components/FollowProfile';
import ProfileRating from 'components/profile/ProfileRating';
import styles from 'styles/components/gigs/Gig.module.scss';

const size = { width: 30, height: 30 };
const containerStyle = { overflow: 'hidden', padding: 0 };
const avatarFullSize = { width: '100%', height: '100%' };
const marginVertical = { margin: '20px 0' };
const style = { textAlign: 'center' };
const margin = { margin: '20px 0' };
const padding = { padding: '20px 10px' };

const InfluencerProfileCardList = ({
  inputValue,
  selectedNetworkIds,
  data,
  setData,
  isLoading,
  hasMore,
  loadMore,
  gig,
  pricingMap,
}) => {
  const [openInvite, setOpenInvite] = useState(false);
  const setProfile = profile =>
    setData(prevState => prevState.map(p => (p.id === profile.id ? profile : p)));
  const noFilter = inputValue.length !== 0 || selectedNetworkIds.length !== 0;
  const noMatches = !isLoading && !hasMore && noFilter && data.length > 0;

  const getPercentageDifference = profile => {
    if (!pricingMap[profile.id]) return;

    const defaultBudget = +gig.budget;
    const profileBudget = pricingMap[profile.id].budget;

    if (defaultBudget === profileBudget) return;

    return <PercentageDifference defaultBudget={defaultBudget} profileBudget={profileBudget} />;
  };

  const isC4Connected = profile =>
    getOnlyFansConnectionId(profile.influencer_networks) &&
    selectedNetworkIds.includes(ONLYFANS_NETWORK_ID);

  const getBorderStyle = profile => {
    const isAmbassador = profile.ambassador;

    if (isAmbassador) {
      return styles.ambassadorBorder;
    } else if (isC4Connected(profile)) {
      return styles.onlyFansConnected;
    } else return '';
  };

  const getChipStyle = profile => {
    const isAmbassador = profile.ambassador;

    if (isAmbassador || isC4Connected(profile)) {
      return (
        <div style={{ position: 'absolute', top: '-12px', right: 5, zIndex: 2 }}>
          <Chip
            label={isAmbassador ? 'GigSocial Ambassador' : 'OnlyFans Connected'}
            className={isAmbassador ? 'gold-chip' : 'blue-chip'}
          />
        </div>
      );
    } else return '';
  };

  const dispatch = useDispatch();

  const handleDialogClose = () => dispatch(closeModal('Gig Limit'));

  const handleInvite = profile => {
    setOpenInvite(true);

    if (gig.invitations_count === MAX_INVITATIONS_COUNT && !gig.user.partner) {
      return dispatch(openModal({ name: 'Gig Limit' }));
    } else {
      dispatch(openModal({ name: 'Request Gig', data: { gig, profile } }));
      dispatch(setInvitation(profile));
    }
  };

  return (
    <div className={styles.container} style={containerStyle}>
      {isLoading && <CircularProgress style={marginVertical} color="primary" />}

      {isEmpty(gig) && data.length === 0 && !isLoading && (
        <Alert severity="info" style={{ marginTop: 20 }}>
          {inputValue.length === 0 && selectedNetworkIds.length === 0
            ? 'Please select a network or search influencers by name'
            : 'No influencers found'}
        </Alert>
      )}

      {!isEmpty(gig) && data.length === 0 && !isLoading && (
        <Alert severity="info" style={{ marginTop: 20 }}>
          No influencers found
        </Alert>
      )}

      <div style={{ height: '100%' }}>
        <InfiniteScroll
          dataLength={data.length}
          next={loadMore}
          hasMore={hasMore}
          loader={isLoading && <LinearProgress style={margin} color="primary" />}
          endMessage={<p style={style}>{noMatches && 'No more profiles'}</p>}
          style={padding}
        >
          {data.map(profile => (
            <div
              key={profile.id}
              className={clsx(styles.gigCard, getBorderStyle(profile))}
              onClick={() => {
                dispatch(
                  openModal({
                    name: 'Full Profile',
                    data: { username: profile.username, selectedNetworkIds },
                  })
                );
              }}
            >
              {getChipStyle(profile)}

              <div className={styles.imageContainer}>
                {profile.avatar_url && (
                  <Image src={profile.avatar_url} layout="fill" alt="Cover" unoptimized />
                )}
              </div>

              <div className={styles.content}>
                <div className={styles.profileInfo}>
                  <Avatar style={size} src={profile?.avatar_url} alt="Avatar">
                    <NoAvatarImage style={avatarFullSize} />
                  </Avatar>
                  <span>{profile.display_name}</span>

                  <FollowProfile profile={profile} circleHeart setProfile={setProfile} />
                </div>

                <NetworksWithFollowers profile={profile} />

                {!isEmpty(gig) ? (
                  <div style={{ display: 'flex' }}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleInvite(profile);
                      }}
                    >
                      Invite
                    </Button>

                    {pricingMap[profile.id] && (
                      <div className={styles.budgetPercentage}>
                        <Chip
                          size="medium"
                          label={`$${pricingMap[profile.id].budget}`}
                          className="light-purple-chip"
                        />

                        {getPercentageDifference(profile)}
                      </div>
                    )}
                  </div>
                ) : (
                  <WorkingStats
                    working_stats={profile.working_stats}
                    newStyles={styles.influencerInfo}
                    showAvgResponseTime={false}
                    showWorkedWith={false}
                  />
                )}

                <div className={styles.chipContainer}>
                  <ProfileRating newStyles={styles} rating={profile.success_score} />
                  <Chip
                    icon={getBadgeIcon(profile.badge)}
                    label={profile.badge}
                    className="light-purple-chip"
                  />
                </div>
              </div>
            </div>
          ))}
        </InfiniteScroll>
      </div>

      <InfluencerProfileDialog handleInvite={handleInvite} gig={gig} />

      {openInvite && <GigInvitationDialog setData={setData} />}

      <SimpleDialog
        modalName="Gig Limit"
        description="You have reached the limit of gig invitations for this gig."
        onClick={handleDialogClose}
        buttonText="Got it"
      />
    </div>
  );
};

InfluencerProfileCardList.propTypes = {
  data: PropTypes.array.isRequired,
  setData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  inputValue: PropTypes.string,
  selectedNetworkIds: PropTypes.array,
  hasMore: PropTypes.bool,
  loadMore: PropTypes.func,
  gig: PropTypes.object,
  pricingMap: PropTypes.object,
};

InfluencerProfileCardList.defaultProps = {
  inputValue: '',
  selectedNetworkIds: [],
  hasMore: false,
  loadMore: () => {},
  gig: {},
  pricingMap: {},
};

export default InfluencerProfileCardList;
